import { FC, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { qrMenuPOST } from 'featureSliced/shared/api/qrMenu/POST'
import { IPaymentComplete } from 'featureSliced/shared/api/qrMenu/types'
import { useMySelector } from 'featureSliced/shared/hooks/useMySelector'
import { useOpenModal } from '../../featureSliced/shared/hooks/useOpenModal'
import { useTranslations } from '../../featureSliced/shared/hooks/useTranslations'
import { RenderIf } from '../RenderIf'
import { ReceiptError } from './ReceiptError'
import { ReceiptLoading } from './ReceiptLoading'
import { ConfirmationModal } from 'featureSliced/features/ConfirmationModal'

import { useMyDispatch } from 'featureSliced/shared/hooks/useMyDispatch'
import { QRMBucketSlice } from 'featureSliced/shared/store/reducers/QRMenuBucketReducer'
import { LoggedOutSuccess } from './SuccessLayouts/LoggedOut'
import { PaymentFailed } from './PaymentFailed'
import { LoggedInSuccess } from './SuccessLayouts/LoggedIn'
import { ToastsSlice } from 'featureSliced/shared/store/reducers/ToasterReducer'
import {
  downloadBlob,
  fetchPdfBlob,
} from 'featureSliced/shared/utils/downloadFromURL'
import moment from 'moment'
import { AnimationData } from 'featureSliced/shared/utils/animations'
import { loadAnimation } from 'featureSliced/shared/utils/loadAnimation'

type ReceiptModalType = {
  paymentKey?: string | null // for HS-Pay
  onCloseModal: (orderGuid?: string) => void
}

type IStatus = 'isLoading' | 'success' | 'error' | 'paymentError'

export const ReceiptModal: FC<ReceiptModalType> = ({
  paymentKey = null,
  onCloseModal,
}) => {
  // Hooks
  const t = useTranslations()
  const dispatch = useMyDispatch()
  const openModal = useOpenModal()

  // State
  const { paymentVariant } = useMySelector((state) => state.app.menu)
  const isUserAuth = useMySelector((state) => state.auth.isLoggedIn)
  const appTheme = useMySelector((state) => state.app.interfaceStyles.appTheme)

  // Actions
  const clearBucket = QRMBucketSlice.actions.clearBucket
  const openToast = ToastsSlice.actions.openToast

  // Search params
  const [searchParams] = useSearchParams()
  const datatransTrxId = searchParams.get('datatransTrxId')
  const saferPayOrderGuid = searchParams.get('orderGuid')
  const redirectResult = searchParams.get('redirectResult')

  // State
  const [isSaveLoading, setIsSaveLoading] = useState(false)
  const [status, setStatus] = useState<IStatus>('isLoading')
  const [receiptData, setReceiptData] = useState<IPaymentComplete>()

  // useEffects
  useEffect(() => {
    triggerPaymentComplete()
  }, [])

  // Functions
  const triggerPaymentComplete = async (index = 0) => {
    if ((paymentKey || datatransTrxId || saferPayOrderGuid) && paymentVariant) {
      try {
        const qrmResult = await qrMenuPOST.paymentComplete(
          paymentVariant as 1 | 2 | 3,
          paymentKey || datatransTrxId,
          saferPayOrderGuid,
          redirectResult
        )
        if (
          qrmResult?.OrderGUID &&
          qrmResult?.TableID &&
          paymentVariant === 2
        ) {
          qrMenuPOST.closeCloudOrders(qrmResult?.OrderGUID, qrmResult?.TableID)
        }
        if (
          qrmResult?.Completed === 1 ||
          qrmResult?.completed === 1 ||
          qrmResult?.total
        ) {
          setReceiptData(qrmResult)
          setStatus('success')
          dispatch(clearBucket())
        } else if (
          qrmResult.paymentStatus === 'failed' ||
          qrmResult.paymentStatus === 'canceled'
        ) {
          console.log('PAYMENT FAILED')
          setStatus('paymentError')
        } else {
          setStatus('error')
          openRetryConfirmation(index + 1)
        }
      } catch (e: any) {
        if (
          e?.data?.paymentStatus === 'failed' ||
          e?.data?.paymentStatus === 'canceled'
        ) {
          console.log('PAYMENT FAILED')
          setStatus('paymentError')
        } else {
          setStatus('error')
          openRetryConfirmation(index + 1)
        }
      }
    }
  }

  const openRetryConfirmation = async (index = 0) => {
    let failedOrderJSON: AnimationData | null = null
    try {
      failedOrderJSON = await loadAnimation(`${appTheme}/callWaiter.json`)
    } catch {
      failedOrderJSON = await loadAnimation('light/failedOrder.json')
    }

    openModal({
      id: `CONFIRMATION_FAIL_${index}`,
      title: '',
      components: [
        ({ closeThisModal }) => (
          <ConfirmationModal
            onConfirm={() => {
              triggerPaymentComplete(index)
              closeThisModal()
            }}
            onCancel={() => {
              onCloseModal()
              closeThisModal()
            }}
            okText={t('buttons.tryAgain')}
            animationJSON={failedOrderJSON}
            text={t('errors.confirmFail')}
          />
        ),
      ],
    })
  }

  ///

  const checkPdfAvailability = async (url: string) => {
    try {
      const response = await fetch(url, { method: 'HEAD' })
      return (
        response.ok && response.headers.get('content-type')?.includes('pdf')
      )
    } catch (error) {
      return false
    }
  }

  const openFailToast = () => {
    dispatch(
      openToast({
        // TODO: translations
        message: t('errors.failedToLoadReceipt'),
        type: 'error',
      })
    )
  }

  const saveReceipt = async () => {
    if (receiptData?.receiptUrl && !isSaveLoading) {
      try {
        setIsSaveLoading(true)
        let pdfFound = false
        const startTime = Date.now()
        const TIMEOUT_MS = 40000 // 40 seconds

        while (Date.now() - startTime < TIMEOUT_MS) {
          const isPdfAvailable = await checkPdfAvailability(
            receiptData.receiptUrl
          )
          console.log(' isPdfAvailable:', isPdfAvailable)
          if (isPdfAvailable) {
            const blob = await fetchPdfBlob(receiptData.receiptUrl)
            const fileName = `Beleg_${
              receiptData?.receiptNr || receiptData?.orderNr
            }_${moment().format('DDMMYY')}.pdf`
            downloadBlob(blob, fileName)
            pdfFound = true
            break
          }
          await new Promise((res) => setTimeout(res, 1000)) // Wait 1 second before retrying
        }

        onCloseModal?.(receiptData?.OrderGUID)
        if (!pdfFound) {
          openFailToast()
        }
      } catch (e) {
        console.log('Error:', e)
      } finally {
        setIsSaveLoading(false)
      }
    } else {
      openFailToast()
    }
  }

  return (
    <>
      <RenderIf condition={status === 'isLoading'}>
        <ReceiptLoading />
      </RenderIf>
      <RenderIf condition={status === 'error'}>
        <ReceiptError onCloseModal={onCloseModal} />
      </RenderIf>
      <RenderIf condition={status === 'paymentError'}>
        <PaymentFailed onCloseModal={onCloseModal} />
      </RenderIf>
      <RenderIf condition={status === 'success'}>
        <RenderIf condition={!isUserAuth}>
          <LoggedOutSuccess
            saveReceipt={saveReceipt}
            isSaveLoading={isSaveLoading}
            receiptData={receiptData}
            onCloseParentModal={onCloseModal}
          />
        </RenderIf>
        <RenderIf condition={isUserAuth}>
          <LoggedInSuccess
            saveReceipt={saveReceipt}
            isSaveLoading={isSaveLoading}
            closeParentModal={onCloseModal}
          />
          {/* <ConfirmationModal
            text="The payment is completed. You can check your order in Orders History."
            animationJSON={historyGuideAnimation}
            onCancel={onCloseModal}
            onConfirm={onCloseModal}
            isBigAnimationIncluded
          /> */}
        </RenderIf>
      </RenderIf>
    </>
  )
}
